import Drawer from "./Drawer";
import { InformationCircleIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StatusSelect } from "./StatusSelect";
import { arrayHasItems } from "../helpers/common";
import { enumStatuses } from "../consts/status";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import VariantModal from "./inventory/VariantModal";
import { useDispatch, useSelector } from "react-redux";
import { ProductLoadingKeys, updateSourceSkuByProduct } from "../slices/productsSlice";
import { IsAmazon } from "./common/IsAmazon";
import { IsReplanishable } from "./common/IsReplanishable";
import { SourceSKU } from "./common/SourceSKU";
import { IsKit } from "./common/IsKit";
import ReactTooltip from "react-tooltip";
import { ACTIVE } from "../consts/inventoryStatus";
import { IsVirtual } from "./common/IsVirtual";
import { IsOverriddenBySourceSkus } from "./common/IsOverriddenBySourceSkus";
import { ConfrimModal } from "./ConfrimModal";

const SIDEBAR_WIDTH_KEY = 'productCostSidebarWidth';
const MIN_WIDTH = 600;
const MAX_WIDTH = 1500;

export const ProductCostSidebar = ({
  onClose,
  access,
  pc,
  findProductBySkuCallback,
  changeProductStatusCallback,
  updateSingleProductCallback
}) => { 
    const sidebarRef = useRef(null);
    const dispatch = useDispatch();
    const {
      loading: productLoading,
      loadingKeys: productLoadingKeys
    } = useSelector((state) => state.products);

    const [sidebarWidth, setSidebarWidth] = useState(() => {
      const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_KEY);
      if (savedWidth) {
        const width = parseInt(savedWidth);
        return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
      }
      return 600;
    });

    const [isResizing, setIsResizing] = useState(false);
    const [virtualLinkageCheckLoading, setVirtualLinkageCheckLoading] = useState(false);
    const [virtualConfirmOpen, setVirtualConfirmOpen] = useState(false);
    const [virtualUsageRows, setVirtualUsageRows] = useState([]);
    const virtualConfirmResolverRef = useRef(null);

    useEffect(() => {
        const handleScroll = (e) => {
          if (sidebarRef.current && sidebarRef.current.contains(e.target)) {
            e.stopPropagation();
          }
        };
        document.addEventListener('scroll', handleScroll, true);
        return () => {
          document.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    useEffect(() => {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
    }, [sidebarWidth]);

    useEffect(() => {
      const handleMouseMove = (e) => {
        if (!isResizing) return;
        
        const newWidth = window.innerWidth - e.clientX;
        const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
        setSidebarWidth(clampedWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
      };

      if (isResizing) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }, [isResizing]);

    const handleResizeStart = (e) => {
      e.preventDefault();
      setIsResizing(true);
    };

    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);

    const clearVirtualConfirmState = (allowed = false) => {
      setVirtualConfirmOpen(false);
      setVirtualUsageRows([]);
      if (virtualConfirmResolverRef.current) {
        virtualConfirmResolverRef.current(allowed);
        virtualConfirmResolverRef.current = null;
      }
    };

    const requestVirtualChangeConfirmation = (linkedRows) => {
      setVirtualUsageRows(linkedRows);
      setVirtualConfirmOpen(true);
      return new Promise((resolve) => {
        virtualConfirmResolverRef.current = resolve;
      });
    };

    const handleBeforeVirtualChange = async ({ productId }) => {
      if (!productId) return true;
      const isCurrentlyVirtual = Boolean(pc?.isVirtual);
      setVirtualLinkageCheckLoading(true);
      try {
        const { data } = await axios.get("/api/v1/product-catalog/amazon-sku-mapping");
        const rows = Array.isArray(data?.items) ? data.items : [];
        const linkedRows = rows.filter(
          (row) => String(row?.execution_product_id || "") === String(productId)
        );
        if (!linkedRows.length || !isCurrentlyVirtual) return true;
        const uniqueLinkedRows = linkedRows.filter((row, index, allRows) => {
          const key = `${row.supply_product_id}-${row.type}-${row.execution_product_id}`;
          return (
            index ===
            allRows.findIndex(
              (candidate) =>
                `${candidate.supply_product_id}-${candidate.type}-${candidate.execution_product_id}` === key
            )
          );
        });
        return await requestVirtualChangeConfirmation(uniqueLinkedRows);
      } catch (error) {
        console.error("Failed to check FBA/FBM links before virtual toggle:", error);
        return true;
      } finally {
        setVirtualLinkageCheckLoading(false);
      }
    };

    useEffect(() => {
      return () => {
        if (virtualConfirmResolverRef.current) {
          virtualConfirmResolverRef.current(false);
          virtualConfirmResolverRef.current = null;
        }
      };
    }, []);

    const handleAddVariant = async (variant) => {
        if (access === "edit") {
            try {
                await dispatch(updateSourceSkuByProduct({
                    productId: pc.id,
                    sourceSku: variant.id,
                    type: 'add'
                }));
                await updateSingleProductCallback?.();
                setIsVariantModalOpen(false);
            } catch (error) {
                console.error("Error updating source SKU:", error);
            }
        }
    }
 
    const productAmazonIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateAmazon);

    const productVirtualIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateVirtual);

    const productOverriddenBySourceSkuIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateOverriddenBySourceSku);

    const productKitIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateKit);

    const productReplenishableIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateReplanishable);

    const productSourceSkuIsLoading = () => 
      productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateSourceSku);

    if (!pc) return null

    return (
        <div
            ref={sidebarRef}
            className="fixed inset-y-0 right-0 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.3)]"
            style={{ width: `${sidebarWidth}px` }}
        >
            <div
                className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize transition-colors duration-200"
                onMouseDown={handleResizeStart}
            />
            <div className="bg-white h-full flex flex-col">
                <div className="flex-shrink-0 bg-white p-6 pb-4 border-b border-gray-100">
                    <section className="flex items-center space-x-4">
                        <h2 className="text-2xl font-bold">History</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 absolute top-4 right-4"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </section>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <span>Product Status:</span>
                        <StatusSelect
                            idItem={pc?.id}
                            access={access}
                            currentStatus={pc?.statusBeingPhasedOut}
                            handleStatusChange={(id, status) =>
                                changeProductStatusCallback(id, status)
                            }
                        />
                    </div>
                    <div className="mt-2 flex justify-center">
                        {pc?.productImage && (
                            <img
                                src={pc?.productImage}
                                className="rounded-md w-auto h-60"
                                onError={(e) => {
                                    e.target.src = null;
                                    e.target.style.height = 0;
                                }}
                            />
                        )}
                    </div>
                    <section className="text-sm text-gray-500 mt-2">
                        <p>Product name: {pc.productName}</p>
                        <p>SKU: {pc.sku}</p>
                        <p>Barcode: {pc.productBarcode}</p>
                        <p>Amazon: {pc.isAmazon === true ? "Yes" : "No"}</p>
                        {arrayHasItems(pc.kitProducts) && (
                            <div className="flex flex-row gap-1">
                                <p>Kits: Yes</p>
                                <InformationCircleIcon
                                    className={`h-5 w-5 text-black-600 hover:text-black-900`}
                                    data-for='kit-products'
                                    data-tip={true}
                                />
                                <ReactTooltip 
                                    id={'kit-products'} 
                                    html={true}
                                    effect='solid'
                                >
                                    {`
                                        <div class="w-72">
                                            ${(pc.kitProducts || []).map((kit) => {
                                                return (
                                                    `<div>${kit.name}(${kit.sku})</div>`
                                                )
                                            }).join("")}
                                        </div>
                                    `}
                                </ReactTooltip>
                            </div>
                        )}
                    </section>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <span>Is Virtual:</span>
                        <IsVirtual
                            product={pc}
                            access={access}
                            isLoading={productVirtualIsLoading() || virtualLinkageCheckLoading}
                            updateCallback={updateSingleProductCallback}
                            containerClassname="flex-row-reverse"
                            beforeChange={handleBeforeVirtualChange}
                        />
                    </div>
                    {!arrayHasItems(pc.kitProducts) && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                            <span>Is Kit:</span>
                            <IsKit
                                product={pc}
                                access={access}
                                isLoading={productKitIsLoading()}
                                updateCallback={updateSingleProductCallback}
                                containerClassname="flex-row-reverse"
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <span>Is Amazon:</span>
                        <IsAmazon
                            product={pc}
                            access={access}
                            isLoading={productAmazonIsLoading()}
                            updateCallback={updateSingleProductCallback}
                            containerClassname="flex-row-reverse"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <span>Is Replenishable:</span>
                        <IsReplanishable
                            product={pc}
                            access={access}
                            isLoading={productReplenishableIsLoading()}
                            updateCallback={updateSingleProductCallback}
                            disabled={pc.statusBeingPhasedOut !== ACTIVE}
                            containerClassname="flex-row-reverse"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                        <span className="whitespace-nowrap">Overridden by Source SKU:</span>
                        <IsOverriddenBySourceSkus
                            product={pc}
                            access={access}
                            isLoading={productOverriddenBySourceSkuIsLoading()}
                            updateCallback={updateSingleProductCallback}
                            containerClassname="flex-row-reverse"
                        />
                    </div>
                    {(!pc?.isReplenishable || pc.isManualKit || pc.isOverriddenBySourceSkus) && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                            <span className="whitespace-nowrap">Source SKU:</span>
                            <SourceSKU
                                key={pc?.sourceSkus?.length}
                                access={access}
                                isLoading={productSourceSkuIsLoading()}
                                product={pc}
                                openModalCallback={() => setIsVariantModalOpen(true)}
                                updateCallback={updateSingleProductCallback}
                            />
                        </div>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto p-6 pt-4">
                    <div className="flex flex-col gap-4">
                        {arrayHasItems(pc?.kitProducts || []) && (
                            <section>
                                <h3 className="text-lg font-semibold mb-2">Kit Products</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 relative action-table">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[135px]">Product Name</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4/12">SKU</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-4/12">Barcode</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]">Unit Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[135px]">BOM packaging</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Landed Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[115px]">Tariff Each</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Shipping Each</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Weighted Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">Received/Ordered</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20px]">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                        {(pc.kitProducts || []).map((kitItem, index) => {
                                            return (
                                            <tr key={`${kitItem.id}_${index}`}>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.name}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.sku}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.barcode}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    ${kitItem.unitPrice}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.sumConnectedBOM ? `$${kitItem.sumConnectedBOM}` : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.totalLandedCost ? `$${kitItem.totalLandedCost}` : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.tariffCost ? `$${kitItem.tariffCost}` : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                    {kitItem.shippingCost ? `$${kitItem.shippingCost}` : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                    {kitItem.weightedCost ? `$${kitItem.weightedCost}` : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                    {kitItem.rangeReceivedBoxes ? kitItem.rangeReceivedBoxes : "N/A"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 w-[20px] !bg-white">
                                                    <button
                                                        onClick={() => findProductBySkuCallback(kitItem.sku)}
                                                        className="flex justify-center items-center w-full hover:text-indigo-700"
                                                    >
                                                        <MagnifyingGlassIcon className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                            )
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}
                        {arrayHasItems(pc.groupedByPoId || []) && (
                            <section>
                                <h3 className="text-lg font-semibold mb-2">Difference</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 relative">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[105px]">PO Number</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[85px]">PO Date</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Status</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[95px]">Unit Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[135px]">BOM packaging</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Landed Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[115px]">Tariff Each</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Shipping Each</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[125px]">Weighted Cost</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">Received/Ordered</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/4">Difference</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {(pc.groupedByPoId || []).map((groupItem, index) => {
                                                return (
                                                <tr key={`${groupItem.poId}_${index}`}>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        <a 
                                                            href={`/finance?search=${groupItem.poNumber}`} 
                                                            target="_blank"
                                                            className="text-blue-600 hover:text-blue-800"
                                                        >
                                                            {groupItem.poNumber}
                                                        </a>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        {groupItem.poDate}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        {enumStatuses[groupItem.status]}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 min-w-[90px]">
                                                        ${groupItem.unitPerCost}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                        {groupItem.sumConnectedBOM ? `$${groupItem.sumConnectedBOM}` : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 min-w-[90px]">
                                                        {groupItem.landedCost ? `$${groupItem.landedCost}` : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-4/12">
                                                        {groupItem.tariffCost ? `$${groupItem.tariffCost}` : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        {groupItem.shippingCost ? `$${groupItem.shippingCost}` : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        {groupItem.weightedCost ? `$${groupItem.weightedCost}` : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-1/5">
                                                        {groupItem.rangeReceivedBoxes ? groupItem.rangeReceivedBoxes : "N/A"}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-2/4">
                                                        {groupItem.differenceData.toPo !== "-" 
                                                            ? `$${groupItem.differenceData.difference} (${groupItem.differenceData.fromPo} - ${groupItem.differenceData.toPo})` 
                                                            : "-"}
                                                    </td>
                                                </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}
                        {arrayHasItems(pc?.relatedSkus || []) && (
                            <section>
                                <h3 className="text-lg font-semibold mb-2">Related SKU's</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 relative action-table">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] max-w-[140px]">Product Name</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">SKU</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Barcode</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20px]">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {(pc?.relatedSkus || []).map((relatedSku, index) => {
                                                return (
                                                <tr key={`${relatedSku.id}_${index}`}>
                                                    <td className="px-3 py-2 text-sm text-gray-500 min-w-[120px] max-w-[120px]">
                                                        {relatedSku.productName}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 w-1/4">
                                                        {relatedSku.sku}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 w-1/3">
                                                        {relatedSku.barcode}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-500 w-[20px] !bg-white">
                                                        <button
                                                            onClick={() => findProductBySkuCallback(relatedSku.sku)}
                                                            className="flex justify-center items-center w-full hover:text-indigo-700"
                                                        >
                                                            <MagnifyingGlassIcon className="h-4 w-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </div>
            {isVariantModalOpen && access === "edit" && (
                <VariantModal
                    isOpen={isVariantModalOpen}
                    onClose={() => {
                        setIsVariantModalOpen(false);
                    }}
                    onAddVariant={handleAddVariant}
                    isReplenishable={true}
                    defaultSearchTerm={pc.productBarcode || ''}
                />
            )}
            <ConfrimModal
                isOpen={access === "edit" && virtualConfirmOpen}
                title={`This product is linked as FBA/FBM to ${virtualUsageRows.length} product(s) (${virtualUsageRows
                    .slice(0, 3)
                    .map((row) => `${row.type}: ${row.supply_sku || row.canonical_product_name || row.supply_product_id}`)
                    .join(", ")}${virtualUsageRows.length > 3 ? ", ..." : ""}). Are you sure you want to change Is Virtual?`}
                confirmButtonTitle={"Yes, change"}
                isLoading={false}
                submitClick={() => {
                    clearVirtualConfirmState(true);
                }}
                cancelClick={() => {
                    clearVirtualConfirmState(false);
                }}
            />
        </div>
    );
};